/**
 * @file ページ・問画像の LRU キャッシュと先読み。
 *
 * @what: Firebase Storage の画像を path ごとに 1 回だけ取得し、object URL を LRU (16 枚) で
 *   使い回す。取得中の Promise も共有するので、表示と先読みが重なっても取得は 1 回になる。
 *   表示中の画像は参照カウント (acquire/release) で守り、追い出しの対象にしない。
 * @why: ビューワはページ送りのたびに getBytes していて、次のページも戻ったページも毎回
 *   取得待ちになる。隣のページを先に取り、めくりを取得待ちなしにする。object URL は
 *   キャッシュから追い出すときに revoke する。参照カウントを持つのは、うんどうチェックの
 *   カード格子のように上限枚数を超えるサムネイルを同時表示する画面で、表示中の URL が
 *   revoke されて ERR_FILE_NOT_FOUND になる退行 (2026-08-25 代表報告) を防ぐため。
 *   参照つきの枚数ぶんは上限を超えて保持し、release 後に上限まで戻す。
 * @failure: 取得失敗はキャッシュに残さない (次の表示で取り直す)。先読みの失敗は握りつぶす
 *   (表示側が改めて取得し、そこで初めてエラーを見せる)。release は acquire と対で呼ぶ
 *   (二重 release は 0 で止まる)。
 */

import { getBytes, ref as storageRef } from "firebase/storage";
import { getFirebaseStorage } from "@/shared/firebase/client";
import { predecodeImage, resetDecodedImages } from "./imageDecode";

type Entry = { promise: Promise<string>; url: string | null; refs: number };

/** 参照なしエントリ (先読み・表示を終えた画像) の上限枚数。 */
const CAPACITY = 16;

/** Map の挿入順を LRU として使う (触るたびに delete → set で末尾へ移す)。 */
const cache = new Map<string, Entry>();

/** 参照なしの古いエントリから、上限枚数まで revoke して追い出す。 */
function evictOverflow(): void {
  if (cache.size <= CAPACITY) return;
  for (const [key, entry] of cache) {
    if (cache.size <= CAPACITY) break;
    if (entry.refs > 0) continue; // 表示中は追い出さない
    cache.delete(key);
    if (entry.url) URL.revokeObjectURL(entry.url);
    else entry.promise.then((url) => URL.revokeObjectURL(url)).catch(() => {});
  }
}

/** 取得共有の本体。エントリを触って LRU 末尾へ移す。追い出しは呼び出し側で行う。 */
function getOrCreate(imagePath: string, contentType: string): Entry {
  const hit = cache.get(imagePath);
  if (hit) {
    cache.delete(imagePath);
    cache.set(imagePath, hit);
    return hit;
  }
  const entry: Entry = { promise: Promise.resolve(""), url: null, refs: 0 };
  entry.promise = getBytes(storageRef(getFirebaseStorage(), imagePath))
    .then((buf) => {
      const url = URL.createObjectURL(new Blob([buf], { type: contentType }));
      entry.url = url;
      return url;
    })
    .catch((err: unknown) => {
      cache.delete(imagePath);
      throw err;
    });
  cache.set(imagePath, entry);
  return entry;
}

/** 画像を取得して object URL を返す (先読み用。参照は立たない)。 */
export function loadPageImage(imagePath: string, contentType: string): Promise<string> {
  const entry = getOrCreate(imagePath, contentType);
  evictOverflow();
  return entry.promise;
}

/**
 * 表示用に取得する (取得共有は loadPageImage と同じ)。参照を立ててから追い出しを
 * 走らせるので、返った URL は release まで revoke されない。unmount で必ず
 * releasePageImage を対で呼ぶこと。
 */
export function acquirePageImage(imagePath: string, contentType: string): Promise<string> {
  const entry = getOrCreate(imagePath, contentType);
  entry.refs += 1;
  evictOverflow();
  return entry.promise;
}

/** acquire の対。参照が 0 になったエントリはキャッシュに残り、あふれたら追い出される。 */
export function releasePageImage(imagePath: string): void {
  const entry = cache.get(imagePath);
  if (!entry) return;
  entry.refs = Math.max(0, entry.refs - 1);
  evictOverflow();
}

/**
 * 取得を終えた画像の object URL を同期で返す (未取得・取得中は null)。
 * ページ送りで、先読み済みの画像を「読み込み中」を挟まずに出すために使う。
 * 当たったエントリは LRU 末尾へ移す。※なぜなら直後の acquire までの間に、前のページの
 * release が走らせる追い出しで、いま出す URL が revoke されるのを防ぐためである。
 */
export function peekPageImage(imagePath: string): string | null {
  const hit = cache.get(imagePath);
  if (!hit?.url) return null;
  cache.delete(imagePath);
  cache.set(imagePath, hit);
  return hit.url;
}

/**
 * 先読み。既定では取得に続けてデコードも済ませ、めくったときにすぐ表示できるようにする。
 * decode: false は取得だけにする (遠いページ。デコードした画像はメモリを使うため)。
 * 失敗しても何も起こさない (表示時に取り直す)。
 */
export function prefetchPageImage(
  imagePath: string,
  contentType: string,
  { decode = true }: { decode?: boolean } = {},
): void {
  try {
    loadPageImage(imagePath, contentType)
      .then((url) => {
        if (decode) predecodeImage(url);
      })
      .catch(() => {});
  } catch {
    // getFirebaseStorage が使えない環境 (テスト等) でも描画を壊さない
  }
}

/** 全部 revoke して空にする (テストと、資料を離れたあとの明示的な解放用)。 */
export function resetPageImageCache(): void {
  for (const entry of cache.values()) {
    if (entry.url) URL.revokeObjectURL(entry.url);
    else entry.promise.then((url) => URL.revokeObjectURL(url)).catch(() => {});
  }
  cache.clear();
  resetDecodedImages();
}

/**
 * 1枚だけ捨てる (同じパスに上書きしたあと、古い絵を出し続けないため)。表示中でも捨てる。
 * 呼び出し側は release の対を崩さないよう、捨てたあとに acquire し直す。
 */
export function evictPageImage(imagePath: string): void {
  const entry = cache.get(imagePath);
  if (!entry) return;
  cache.delete(imagePath);
  if (entry.url) URL.revokeObjectURL(entry.url);
  else entry.promise.then((url) => URL.revokeObjectURL(url)).catch(() => {});
}
